// ---------------------------------------------------------------------------
// Every AI opponent, at /models.
//
// A real `<table>`, not a grid of divs, for the same reason the histogram is
// one: it is tabular data, a screen reader should get it as rows and columns,
// and the markup should degrade to the numbers rather than to an ARIA string.
//
// The order is points per round, best first. That flatters a small sample by
// construction — three good rounds outrank three hundred decent ones — so the
// rounds count sits in its own column and the note above the table says it
// plainly. Ordering by volume instead would have the opposite problem and no
// note could fix it, because the reader would have no reason to look.
//
// Models that have never played are last, and they are here at all because the
// lobby offers them: a page that listed only what had already happened would
// answer "who can I play against" with silence.
//
// The prices are deliberately absent. They are a fact about the site's budget
// rather than about the opponent, they live in the lobby picker where the choice
// is actually made, and a leaderboard that puts cost beside score invites
// exactly the reading the note above the table is trying to prevent.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ModelSummary } from '@countdown/shared';

import { AppHeader } from '../components/layout/AppHeader.js';
import { Card, CardTitle } from '../components/primitives/Card.js';
import { listModels } from '../lib/api.js';
import { modelName, modelPath } from '../lib/format.js';

type Load =
  | { status: 'loading' }
  | { status: 'failed'; message: string }
  | { status: 'ready'; models: ModelSummary[] };

export function ModelsScreen() {
  const [load, setLoad] = useState<Load>({ status: 'loading' });

  useEffect(() => {
    let live = true;
    listModels()
      .then((models) => {
        if (live) setLoad({ status: 'ready', models });
      })
      .catch((error: Error) => {
        if (live) setLoad({ status: 'failed', message: error.message });
      });
    return () => {
      live = false;
    };
  }, []);

  return (
    <>
      <AppHeader />
      <main className="mx-auto w-full max-w-[860px] px-4 py-6 sm:px-6">
        <header className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold">AI opponents</h1>
          <p className="max-w-[560px] text-sm text-ink-muted text-pretty">
            Every model that has sat at the table here, and every one the lobby
            offers. The letters round is character-level anagramming, which is the
            classic tokenizer blind spot — the spread between the best of these
            and the worst is narrower than their reputations suggest.
          </p>
        </header>

        {load.status === 'loading' && (
          <p className="py-16 text-center text-ink-dim" aria-live="polite">
            Loading the opponents.
          </p>
        )}

        {load.status === 'failed' && (
          <div className="py-16 text-center">
            <h1 className="text-2xl font-bold">That list did not load</h1>
            <p className="mt-3 text-ink-muted">{load.message}</p>
            <Link to="/" className="mt-6 inline-block min-h-11 leading-11 text-blue-200 underline">
              Back to the home page
            </Link>
          </div>
        )}

        {load.status === 'ready' && (
          <div className="mt-5">
            <ModelTable models={load.models} />
          </div>
        )}
      </main>
    </>
  );
}

/** The table itself, without the fetch around it. Exported for the gallery. */
export function ModelTable({ models }: { models: ModelSummary[] }) {
  const played = models.filter((model) => model.roundsPlayed > 0);

  return (
    <Card>
      <CardTitle>By points per round</CardTitle>
      <p className="mt-1 text-xs text-ink-dim">
        Points per round is fair across game lengths, and nothing else here is.
        Read it beside the rounds column: a handful of rounds is not a record.
      </p>

      {played.length === 0 ? (
        <p className="mt-4 text-sm text-ink-dim">
          No AI opponent has finished a game yet. The models below are the ones
          the lobby offers.
        </p>
      ) : null}

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-sm">
          <caption className="sr-only">
            AI opponents by points per round, best first, with never-played models
            last.
          </caption>
          <thead>
            <tr className="border-b border-line text-left">
              <Th>opponent</Th>
              <Th align="right">per round</Th>
              <Th align="right">rounds</Th>
              <Th align="right">games</Th>
              <Th align="right">seats</Th>
              <Th align="right">wins</Th>
            </tr>
          </thead>
          <tbody>
            {models.map((model) => (
              <ModelRow key={model.modelId} model={model} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ModelRow({ model }: { model: ModelSummary }) {
  const path = modelPath(model.modelId);
  const name = modelName(model.modelId, model.label);

  return (
    <tr className="border-b border-line last:border-0" data-model={model.modelId}>
      <th scope="row" className="py-2.5 pr-3 text-left font-medium">
        {/* A model id that does not split into `provider/name` has no route to
            link to, so it renders as a name. See `modelPath`. */}
        {path ? (
          <Link to={path} className="text-blue-200 underline-offset-2 hover:underline">
            {name}
          </Link>
        ) : (
          <span>{name}</span>
        )}
        {model.retired ? (
          <span className="ml-2 font-mono text-[10px] tracking-[0.08em] uppercase text-ink-dim">
            retired
          </span>
        ) : null}
      </th>
      <Td>
        {model.pointsPerRound === null ? (
          <span className="text-ink-dim">never played</span>
        ) : (
          model.pointsPerRound
        )}
      </Td>
      <Td>{model.roundsPlayed}</Td>
      <Td>{model.gamesPlayed}</Td>
      {/* Seats rather than games, because a lobby can seat one model twice and
          then the two numbers are different and both true. */}
      <Td>{model.seatsHeld}</Td>
      <Td>{model.wins}</Td>
    </tr>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      scope="col"
      className={`py-2 ${align === 'right' ? 'pl-3 text-right' : 'pr-3 text-left'} label-mono`}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="tabular py-2.5 pl-3 text-right font-mono">{children}</td>;
}
